/* eslint-disable no-console */
import {useHostName} from 'presentation/hooks';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {useBookingAuthorization} from 'presentation/modules/booking/shared/hooks/useBookingAuthorization';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useQueryParams} from 'presentation/pages';
import {useGetHomeQuery} from 'presentation/pages/private/home/query/useGetHomeQuery';
import {translations} from 'presentation/translations-new';
import {HealthSpecialty, Specialty} from 'presentation/utils';
import {useState} from 'react';

import {TELEMEDICINE_INFO} from 'presentation/modules/booking/shared/constants/telemedicineInfo';
import {useTeaForm} from 'presentation/modules/booking/shared/hooks/useTeaForm';
import {ParsedTelehealthAppointment} from 'presentation/pages/private/home/utils/parseTelehealthAppointments/types';
import {TEA_SPECIALTY_CODE} from './constants';
import {
	CheckBookingModal,
	CheckBookingModalEnum,
	GetHealthBookingBySpecialtyParams,
	HealthAppointmentWithSpecialtyNameSelected,
	UseTelehealthSpecialtiesSelectionProps,
} from './types';

const {loading} = translations['pt-br'].commons;
const {verifyingAuth} = translations['pt-br'].privateFlow.bookingFlow.specialtyFlow;

export function useTelehealthSpecialtiesSelection({
	handleNextStep,
}: UseTelehealthSpecialtiesSelectionProps) {
	const {
		SEARCH_PARAMS: {query},
		clearSearch,
	} = useQueryParams();
	const {isGndiSaoPaulo} = useHostName();
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const [checkBookingModal, setCheckBookingModal] = useState<CheckBookingModal>('none');
	const [scheduledBooking, setScheduledBooking] =
		useState<HealthAppointmentWithSpecialtyNameSelected | null>(null);
	const {verifyHealthAuthorization} = useBookingAuthorization();
	const {handleOpenEmbeddedInfo} = useTeaForm();
	const {refetch: refetchAppointmentsList} = useGetHomeQuery();

	const {specialtyCode, subSpecialtyCode} = bookingData;

	const loadingText =
		checkBookingModal === CheckBookingModalEnum.VERIFYING_AUTH ? verifyingAuth : loading;

	const handleSelectCard = async (specialty: Specialty) => {
		const hasSubSpecialty = 'sub_code' in specialty && specialty.sub_code;

		if (isGndiSaoPaulo && specialty.code === TEA_SPECIALTY_CODE) {
			handleOpenEmbeddedInfo();
			return;
		}

		try {
			const permitMultipleBookings = (specialty as HealthSpecialty).multipleBookings;

			setCheckBookingModal('verifyingDuplicity');
			const {data: updatedAppointmentsList} = await refetchAppointmentsList();

			const alreadyScheduled = permitMultipleBookings
				? false
				: getHealthBookingBySpecialty({
						specialtyCode: specialty.code,
						appointments: updatedAppointmentsList?.appointments,
					});

			setCheckBookingModal('verifyingAuth');
			const {
				content: {specialtyAuthorization},
			} = await verifyHealthAuthorization({
				speciality: specialty.code,
			});
			setCheckBookingModal('none');

			if (!specialtyAuthorization) {
				setCheckBookingModal('needAuth');
				return;
			}

			if (alreadyScheduled) {
				setScheduledBooking({
					...alreadyScheduled,
					subSpecialtyCode: hasSubSpecialty ? specialty.sub_code : '',
					specialtyNameSelected: specialty.formattedText,
				});
				setCheckBookingModal('duplicityState');
				return;
			}

			setBookingData(prev => ({
				...prev,
				specialtyCode: specialty.code,
				specialtyName: specialty.formattedText,
				subSpecialtyCode: hasSubSpecialty ? specialty.sub_code : '',
				unitCode: TELEMEDICINE_INFO.code,
				unitName: TELEMEDICINE_INFO.name,
				professionalCode: '',
				professionalName: '',
				unitAddress: '',
				dateAt: '',
				hourAt: '',
				multipleBookings: (specialty as HealthSpecialty).multipleBookings,
			}));

			clearSearch();
			handleNextStep();
		} catch (error) {
			console.error(error);
			setCheckBookingModal('error');
		}
	};

	const getHealthBookingBySpecialty = ({
		specialtyCode,
		appointments,
	}: GetHealthBookingBySpecialtyParams) => {
		return appointments?.find(
			booking =>
				booking.specialtyCode === specialtyCode &&
				(booking.bookingType === BookingTypeEnum.HEALTH ||
					booking.bookingType === BookingTypeEnum.TELEHEALTH),
		) as ParsedTelehealthAppointment | undefined;
	};

	const resetBookingDataSpecialties = () => {
		setBookingData(prev => ({
			...prev,
			specialtyCode: '',
			subSpecialtyCode: undefined,
		}));
	};

	const handleChooseScriptSuggestion = async () => {
		try {
			setCheckBookingModal('verifyingDuplicity');
			const {data: updatedAppointmentsList} = await refetchAppointmentsList();
			const alreadyScheduled = getHealthBookingBySpecialty({
				specialtyCode: String(bookingData.specialtyCode),
				appointments: updatedAppointmentsList?.appointments,
			});
			setCheckBookingModal('none');

			if (alreadyScheduled) {
				setScheduledBooking(alreadyScheduled);
				setCheckBookingModal('duplicityState');
				return;
			}
			handleNextStep();
		} catch (error) {
			console.error(error);
			setCheckBookingModal('error');
		}
	};

	const handleCheckTelehealthDuplicity = async (specialtyCode: string) => {
		try {
			setCheckBookingModal('verifyingDuplicity');
			const {data: updatedAppointmentsList} = await refetchAppointmentsList();
			const alreadyScheduled = getHealthBookingBySpecialty({
				specialtyCode,
				appointments: updatedAppointmentsList?.appointments,
			});
			setCheckBookingModal('none');
			if (alreadyScheduled) {
				setScheduledBooking(alreadyScheduled);
				setCheckBookingModal('duplicityState');
				return true;
			}
			return false;
		} catch (error) {
			console.error(error);
			setCheckBookingModal('error');
			return true;
		}
	};

	return {
		query,
		handleSelectCard,
		checkBookingModal,
		scheduledBooking,
		setCheckBookingModal,
		specialtyCode,
		subSpecialtyCode,
		loadingText,
		resetBookingDataSpecialties,
		handleChooseScriptSuggestion,
		handleCheckTelehealthDuplicity,
	};
}
